import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/axios';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,   setUser]   = useState(() => JSON.parse(localStorage.getItem('auth_user')   || 'null'));
  const [tenant, setTenant] = useState(() => JSON.parse(localStorage.getItem('auth_tenant') || 'null'));
  const [loading, setLoading] = useState(false);

  const persist = useCallback((u, t) => {
    setUser(u);
    setTenant(t);
    if (u) localStorage.setItem('auth_user',   JSON.stringify(u));
    else    localStorage.removeItem('auth_user');
    if (t) localStorage.setItem('auth_tenant', JSON.stringify(t));
    else    localStorage.removeItem('auth_tenant');
  }, []);

  // Listen for forced logout events fired by the axios interceptor
  useEffect(() => {
    const handle = () => persist(null, null);
    window.addEventListener('auth:logout', handle);
    return () => window.removeEventListener('auth:logout', handle);
  }, [persist]);

  // On mount: if we have a stored user but no fresh access token, refresh silently
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token && user) {
      api.post('/auth/refresh').then((r) => {
        localStorage.setItem('access_token', r.data.data.accessToken);
      }).catch(() => persist(null, null));
    }
  }, []); // eslint-disable-line

  async function login(email, password) {
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', { email, password });
      localStorage.setItem('access_token', data.data.accessToken);
      persist(data.data.user, data.data.tenant);
      return data.data;
    } finally {
      setLoading(false);
    }
  }

  async function signup(payload) {
    setLoading(true);
    try {
      const { data } = await api.post('/auth/signup', payload);
      localStorage.setItem('access_token', data.data.accessToken);
      persist(data.data.user, data.data.tenant);
      return data.data;
    } finally {
      setLoading(false);
    }
  }

  async function logout() {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    localStorage.removeItem('access_token');
    persist(null, null);
  }

  async function refreshMe() {
    const { data } = await api.get('/auth/me');
    persist(data.data.user, data.data.tenant);
    return data.data;
  }

  const isAuthenticated = !!user && !!localStorage.getItem('access_token');

  return (
    <AuthContext.Provider value={{ user, tenant, loading, isAuthenticated, login, signup, logout, refreshMe, setTenant }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
